import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { UploadCloud, FileText, X, Sparkles, User, Briefcase, Users, MessageSquarePlus } from "lucide-react";
import { motion } from "framer-motion";
import BackToDashboardButton from "@/components/BackToDashboardButton";

const UploadScreen = ({ onGenerate, initialInfo, onBackToDashboard }) => {
  const [files, setFiles] = useState([]);
  const [pastedText, setPastedText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  // Metadata fields — pré-remplis depuis le formulaire client quand il existe
  const [prenomNom, setPrenomNom] = useState(initialInfo?.prenomNom || "");
  const [activite, setActivite] = useState(initialInfo?.activite || "");
  const [publicCible, setPublicCible] = useState(initialInfo?.publicCible || "");
  const [instructionsSupp, setInstructionsSupp] = useState("");

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      // Handle rejected files
      return;
    }
    setFiles((prev) => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/plain": [".txt"],
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
      "application/msword": [".doc"],
    },
    multiple: true,
  });

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGenerate = async () => {
    if (files.length === 0 && !pastedText.trim()) {
      return;
    }
    setIsLoading(true);
    await onGenerate(files, pastedText, {
      prenomNom,
      activite,
      publicCible,
      instructionsSupp,
    });
    setIsLoading(false);
  };

  const hasContent = files.length > 0 || pastedText.trim().length > 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="mx-auto max-w-5xl px-6 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="font-heading text-xl font-semibold">Autobiographe Pro</h1>
            <p className="text-sm text-muted-foreground">Vos sources, votre histoire</p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {onBackToDashboard && (
            <div className="mb-4">
              <BackToDashboardButton onClick={() => onBackToDashboard()} />
            </div>
          )}
          <div className="mb-8">
            <h2 className="font-heading text-3xl font-semibold mb-2">Sources</h2>
            <p className="text-muted-foreground">
              Ajoutez vos fichiers d'interview, notes ou transcriptions pour générer votre autobiographie.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Dropzone */}
            <Card className="border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-medium flex items-center gap-2">
                  <UploadCloud className="w-5 h-5 text-primary" />
                  Téléverser des fichiers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  {...getRootProps()}
                  className={`dropzone-container p-8 text-center cursor-pointer ${
                    isDragActive ? "active" : ""
                  }`}
                  data-testid="dropzone"
                >
                  <input {...getInputProps()} />
                  <UploadCloud className="mx-auto w-12 h-12 text-muted-foreground mb-3" />
                  <p className="font-medium text-foreground mb-1">
                    {isDragActive ? "Déposez les fichiers ici" : "Glissez-déposez vos fichiers"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Formats acceptés : .txt, .pdf, .docx
                  </p>
                </div>

                {/* File list */}
                {files.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {files.map((file, index) => (
                      <div
                        key={index}
                        className="file-chip"
                        data-testid={`file-chip-${index}`}
                      >
                        <FileText className="w-4 h-4" />
                        <span className="truncate max-w-[180px]">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="remove-btn"
                          aria-label={`Supprimer ${file.name}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Paste text */}
            <Card className="border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-medium flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Coller du texte
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={pastedText}
                  onChange={(e) => setPastedText(e.target.value)}
                  placeholder="Collez ici vos transcriptions, notes ou textes bruts..."
                  className="min-h-[200px] resize-none"
                  data-testid="paste-textarea"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  {pastedText.length} caractères
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Metadata fields */}
          <Card className="mt-6 border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-medium">Informations (optionnel)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="prenom-nom" className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Prénom Nom
                  </Label>
                  <Input
                    id="prenom-nom"
                    value={prenomNom}
                    onChange={(e) => setPrenomNom(e.target.value)}
                    placeholder="Ex: Marie Dupont"
                    data-testid="input-prenom-nom"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="activite" className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Activité
                  </Label>
                  <Input
                    id="activite"
                    value={activite}
                    onChange={(e) => setActivite(e.target.value)}
                    placeholder="Ex: Consultante en transformation digitale"
                    data-testid="input-activite"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="public-cible" className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Public cible
                  </Label>
                  <Input
                    id="public-cible"
                    value={publicCible}
                    onChange={(e) => setPublicCible(e.target.value)}
                    placeholder="Ex: Entrepreneurs et PME"
                    data-testid="input-public-cible"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Instructions complémentaires */}
          <Card className="mt-6 border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-medium flex items-center gap-2">
                <MessageSquarePlus className="w-5 h-5 text-primary" />
                Instructions complémentaires (optionnel)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={instructionsSupp}
                onChange={(e) => setInstructionsSupp(e.target.value)}
                placeholder="Ex: Mets l'accent sur les histoires de résilience. Utilise un ton plus formel. Évite de mentionner la période universitaire..."
                className="min-h-[100px] resize-none"
                data-testid="instructions-textarea"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Ces instructions guideront la génération de votre autobiographie.
              </p>
            </CardContent>
          </Card>

          {/* Generate button */}
          <div className="mt-8 flex justify-center">
            <Button
              onClick={handleGenerate}
              disabled={!hasContent || isLoading}
              className="h-14 px-10 text-lg btn-transition"
              data-testid="upload-start-button"
            >
              {isLoading ? (
                <>
                  <span className="animate-spin mr-2">&#9696;</span>
                  Préparation...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Générer l'autobiographie
                </>
              )}
            </Button>
          </div>

          {!hasContent && (
            <p className="text-center text-sm text-muted-foreground mt-4">
              Ajoutez au moins un fichier ou du texte pour continuer.
            </p>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default UploadScreen;
